import { useEffect, useRef, useState } from 'react';
import { GameProps } from './types';
import { TouchControls } from './TouchControls';

interface Point {
  x: number;
  y: number;
}

type SnakePhase = 'intro' | 'playing' | 'gameover';
type Direction = 'up' | 'down' | 'left' | 'right';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const directionVectors: Record<Direction, Point> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

const isReverseDirection = (current: Direction, next: Direction) => {
  return (
    (current === 'up' && next === 'down') ||
    (current === 'down' && next === 'up') ||
    (current === 'left' && next === 'right') ||
    (current === 'right' && next === 'left')
  );
};

const createInitialSnake = (cols: number, rows: number): Point[] => {
  const centerX = Math.floor(cols / 2);
  const centerY = Math.floor(rows / 2);

  return [
    { x: centerX, y: centerY },
    { x: centerX - 1, y: centerY },
    { x: centerX - 2, y: centerY },
  ];
};

export function SnakeGame({ fgColor, bgColor, softColor, onExit }: GameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const stateRef = useRef({
    phase: 'intro' as SnakePhase,
    width: 0,
    height: 0,
    deviceRatio: 1,
    cellSize: 20,
    cols: 20,
    rows: 16,
    snake: [] as Point[],
    food: { x: 0, y: 0 },
    direction: 'right' as Direction,
    nextDirection: 'right' as Direction,
    score: 0,
    stepInterval: 140,
    lastStepAt: 0,
  });

  const resetRound = () => {
    const state = stateRef.current;
    state.phase = 'intro';
    state.direction = 'right';
    state.nextDirection = 'right';
    state.score = 0;
    state.stepInterval = 140;

    const snake = createInitialSnake(state.cols, state.rows);
    state.snake = snake;

    let foodPlaced = false;
    while (!foodPlaced) {
      const candidate = {
        x: Math.floor(Math.random() * state.cols),
        y: Math.floor(Math.random() * state.rows),
      };
      const overlapsSnake = snake.some((segment) => segment.x === candidate.x && segment.y === candidate.y);
      if (!overlapsSnake) {
        state.food = candidate;
        foodPlaced = true;
      }
    }
    state.lastStepAt = 0;
  };

  const resizeCanvas = () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) {
      return;
    }

    const bounds = container.getBoundingClientRect();
    const width = Math.max(240, Math.floor(bounds.width));
    const height = Math.max(180, Math.floor(bounds.height));
    const deviceRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(width * deviceRatio);
    canvas.height = Math.floor(height * deviceRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const state = stateRef.current;
    state.width = width;
    state.height = height;
    state.deviceRatio = deviceRatio;

    const usableHeight = height - 44;
    const cellSize = clamp(Math.floor(Math.min(width / 30, usableHeight / 22)), 12, 26);
    state.cellSize = cellSize;
    state.cols = Math.max(12, Math.floor(width / cellSize));
    state.rows = Math.max(10, Math.floor(usableHeight / cellSize));

    resetRound();
  };

  const updateSnake = (timestamp: number) => {
    const state = stateRef.current;

    if (state.phase !== 'playing') {
      return;
    }

    if (!state.lastStepAt) {
      state.lastStepAt = timestamp;
      return;
    }

    if (timestamp - state.lastStepAt < state.stepInterval) {
      return;
    }

    state.lastStepAt = timestamp;

    if (!isReverseDirection(state.direction, state.nextDirection)) {
      state.direction = state.nextDirection;
    }

    const heading = directionVectors[state.direction];
    const head = state.snake[0];
    const nextHead = {
      x: head.x + heading.x,
      y: head.y + heading.y,
    };

    const hitsWall = nextHead.x < 0 || nextHead.y < 0 || nextHead.x >= state.cols || nextHead.y >= state.rows;
    const hitsSelf = state.snake.some((segment) => segment.x === nextHead.x && segment.y === nextHead.y);

    if (hitsWall || hitsSelf) {
      state.phase = 'gameover';
      return;
    }

    state.snake.unshift(nextHead);

    if (nextHead.x === state.food.x && nextHead.y === state.food.y) {
      state.score += 1;
      state.stepInterval = clamp(140 - state.score * 3, 65, 140);

      let foodPlaced = false;
      while (!foodPlaced) {
        const candidate = {
          x: Math.floor(Math.random() * state.cols),
          y: Math.floor(Math.random() * state.rows),
        };
        const overlapsSnake = state.snake.some((segment) => segment.x === candidate.x && segment.y === candidate.y);
        if (!overlapsSnake) {
          state.food = candidate;
          foodPlaced = true;
        }
      }
    } else {
      state.snake.pop();
    }
  };

  const handleDirection = (direction: Direction, allowRestart: boolean) => {
    const state = stateRef.current;

    if (state.phase === 'gameover') {
      if (!allowRestart) {
        return;
      }
      resetRound();
      state.phase = 'playing';
      state.lastStepAt = 0;
    }

    if (state.phase === 'intro') {
      state.phase = 'playing';
      state.lastStepAt = 0;
    }

    if (state.phase !== 'playing') {
      return;
    }

    state.nextDirection = direction;
  };

  const handleTouchQuit = () => {
    onExit(String(stateRef.current.score));
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const state = stateRef.current;
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    const { width, height, deviceRatio, cellSize } = state;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);

    context.fillStyle = bgColor;
    context.fillRect(0, 0, width, height);

    const topOffset = 34;

    context.globalAlpha = 0.12;
    context.strokeStyle = fgColor;
    for (let x = 0; x <= state.cols; x += 1) {
      const px = x * cellSize;
      context.beginPath();
      context.moveTo(px, topOffset);
      context.lineTo(px, topOffset + state.rows * cellSize);
      context.stroke();
    }
    for (let y = 0; y <= state.rows; y += 1) {
      const py = topOffset + y * cellSize;
      context.beginPath();
      context.moveTo(0, py);
      context.lineTo(state.cols * cellSize, py);
      context.stroke();
    }
    context.globalAlpha = 1;

    context.fillStyle = fgColor;
    state.snake.forEach((segment, index) => {
      const inset = index === 0 ? 1 : 2;
      context.fillRect(
        segment.x * cellSize + inset,
        topOffset + segment.y * cellSize + inset,
        cellSize - inset * 2,
        cellSize - inset * 2
      );
    });

    context.fillStyle = softColor;
    context.fillRect(state.food.x * cellSize + 3, topOffset + state.food.y * cellSize + 3, cellSize - 6, cellSize - 6);

    context.fillStyle = fgColor;
    context.font = '16px "Courier New", monospace';
    context.fillText(`SNAKE  SCORE ${state.score}`, 8, 22);

    if (state.phase === 'intro') {
      context.fillStyle = softColor;
      context.font = '15px "Courier New", monospace';
      context.fillText('Arrow keys to move. Press ENTER to start. Press ESC to quit.', 8, height - 12);
    }

    if (state.phase === 'gameover') {
      context.fillStyle = 'rgba(0, 0, 0, 0.55)';
      context.fillRect(0, 0, width, height);
      context.fillStyle = fgColor;
      context.font = '24px "Courier New", monospace';
      context.fillText('GAME OVER', Math.max(8, width / 2 - 72), height / 2 - 10);
      context.font = '16px "Courier New", monospace';
      context.fillStyle = softColor;
      context.fillText(`Score: ${state.score}`, Math.max(8, width / 2 - 42), height / 2 + 16);
      context.fillText('Press ENTER to restart or ESC to quit', Math.max(8, width / 2 - 158), height / 2 + 44);
    }
  };

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    resizeCanvas();

    const observer = new ResizeObserver(() => resizeCanvas());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    const tick = (timestamp: number) => {
      updateSnake(timestamp);
      draw();
      animationRef.current = window.requestAnimationFrame(tick);
    };

    animationRef.current = window.requestAnimationFrame(tick);

    const handleKeyDown = (event: KeyboardEvent) => {
      const state = stateRef.current;
      const key = event.key;

      if (key === 'Escape') {
        event.preventDefault();
        onExit(String(state.score));
        return;
      }

      if (key === 'ArrowUp') {
        event.preventDefault();
        handleDirection('up', false);
        return;
      }
      if (key === 'ArrowDown') {
        event.preventDefault();
        handleDirection('down', false);
        return;
      }
      if (key === 'ArrowLeft') {
        event.preventDefault();
        handleDirection('left', false);
        return;
      }
      if (key === 'ArrowRight') {
        event.preventDefault();
        handleDirection('right', false);
        return;
      }

      if (state.phase === 'intro' && key === 'Enter') {
        event.preventDefault();
        state.phase = 'playing';
        state.lastStepAt = 0;
      }

      if (state.phase === 'gameover' && key === 'Enter') {
        event.preventDefault();
        resetRound();
        state.phase = 'playing';
        return;
      }

    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      observer.disconnect();
      window.removeEventListener('keydown', handleKeyDown);
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="game-canvas-wrap" ref={containerRef}>
      {isTouchDevice ? <div className="game-touch-instruction">Touch: use D-pad to move. Tap QUIT to exit.</div> : null}
      <canvas ref={canvasRef} className="game-canvas" />
      {isTouchDevice ? (
        <TouchControls
          type="dpad"
          onDirection={(direction) => handleDirection(direction, true)}
          onAction={() => {}}
          onQuit={handleTouchQuit}
        />
      ) : null}
    </div>
  );
}
